import { Settlement } from '../models/Entities/Settlements/Settlement';
import { ProviderAssociations } from '../models/Entities/Settlements/Associations/ProviderAssociations';
import { NewProviderSettlement } from '../models/Entities/Settlements/NewSettlement';
import { SettlementTypeEnum } from '../models/Enums/SettlementTypeEnum';
import { ErrorType } from '../models/HttpError';

/**
 * Tipo de liquidación (spec 010, DEC-APP-004): única fuente para lista, cabecera, desglose, dueño de la factura
 * y detalle. Funciones puras.
 */

export type SettlementKind = 'provider' | 'sponsor' | 'practitioner' | 'agreement' | 'protocol' | 'unknown';

export type SettlementKindInput = Partial<Pick<Settlement, 'settlementType' | 'sponsor' | 'practitioner' | 'protocol' | 'agreements'>> & {
  provider?: ProviderAssociations[] | null;
};

const hasAny = (list: unknown[] | null | undefined): boolean => (list?.filter(Boolean)?.length ?? 0) > 0;

/**
 * `settlementType === 4` → proveedor (el tipo 4 siempre es explícito). Si no, por asociaciones, porque el tipo
 * guardado de las liquidaciones viejas no es confiable (el front las creaba con 0): sponsor → médico + protocolo →
 * obra social → proveedor (respaldo) → sólo protocolo → desconocido.
 */
export const resolveSettlementKind = (settlement: SettlementKindInput | null | undefined): SettlementKind => {
  if (!settlement) return 'unknown';
  if (Number(settlement.settlementType) === SettlementTypeEnum.Provider) return 'provider';
  if (hasAny(settlement.sponsor)) return 'sponsor';
  if (hasAny(settlement.practitioner) && hasAny(settlement.protocol)) return 'practitioner';
  if (hasAny(settlement.agreements)) return 'agreement';
  if (hasAny(settlement.provider)) return 'provider';
  if (hasAny(settlement.protocol)) return 'protocol';
  return 'unknown';
};

export interface SettlementKindMeta {
  /** settlementType.{kind}.label */
  labelKey: string;
  /** settlementType.{kind}.entity */
  entityKey: string;
  /** Ícono de Fluent v8 (`FontIcon`), como los demás tipos de la cabecera. */
  icon: string;
}

const KIND_ICONS: Partial<Record<SettlementKind, string>> = {
  provider: 'DeliveryTruck',
  sponsor: 'ContactCard',
  practitioner: 'UserFollowed',
  agreement: 'Hospital',
};

/** Claves i18n e ícono por tipo; `undefined` para "sólo protocolo" y desconocido (la cabecera no muestra tipo). */
export const settlementKindMeta = (kind: SettlementKind): SettlementKindMeta | undefined => {
  const icon = KIND_ICONS[kind];
  return icon ? { labelKey: `settlementType.${kind}.label`, entityKey: `settlementType.${kind}.entity`, icon } : undefined;
};

/** Primera asociación de proveedor de la liquidación. */
export const providerOf = (settlement: SettlementKindInput | null | undefined): ProviderAssociations | undefined =>
  settlement?.provider?.find(Boolean) ?? undefined;

export interface ProviderSettlementInput {
  provider: { id: number; personIdentifier: string; businessName: string };
  name?: string;
  description?: string;
  /** Período informativo (no filtra consumos). */
  from: Date;
  to: Date;
  /** Monedas de lo que está por liquidar (barra, `byCurrency`). */
  currencies: string[];
}

/**
 * Cuerpo del alta de proveedor (POST settlements/settlements/, tipo 4): omite `name` / `description` vacíos (la API
 * arma el nombre por defecto) y manda `currencyCode` sólo con una única moneda (si no, liquidación multimoneda, D-6).
 */
export const buildProviderSettlement = (input: ProviderSettlementInput): NewProviderSettlement => {
  const body: NewProviderSettlement = {
    settlementType: SettlementTypeEnum.Provider,
    providerId: input.provider.id,
    providerIdentifier: input.provider.personIdentifier,
    businessName: input.provider.businessName,
    settlementDateRangeInit: input.from,
    settlementDateRangeEnd: input.to,
    isManualSettlement: false,
  };
  const name = input.name?.trim();
  if (name) body.name = name;
  const description = input.description?.trim();
  if (description) body.description = description;
  const currencies = Array.from(new Set(input.currencies.map((code) => code?.trim().toUpperCase()).filter(Boolean)));
  if (currencies.length === 1) body.currencyCode = currencies[0];
  return body;
};

/** ErrorType del alta con el `data` del cuerpo de error (409: `{ settlementId }`). */
export type SettlementCreateError = ErrorType & { data?: { settlementId?: number | null } | null };

/** Id de la liquidación de proveedor en curso: `data.settlementId` del 409 (DIS-13). */
export const conflictSettlementIdOf = (error: SettlementCreateError | null | undefined): number | undefined => {
  const id = error?.data?.settlementId;
  return id === null || id === undefined || Number.isNaN(Number(id)) ? undefined : Number(id);
};
